/**
 * AdminLessonContent — ders içerik (phase) editörü.
 * Route: /admin/cabin-curriculum/lessons/:lessonId · Roles: admin, superadmin
 *
 * 7 phase tipi: intro, teaching, multiple_choice, vocabulary_time_race,
 * match_pairs, fill_in_blank, drag_drop_sentence. Şema DB/mobil ile birebir.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/admin/AdminLayout';
import { PageHeader, LoadingState } from '../components/admin';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { useAdminLessonContent, useUpdateLesson } from '../api/admin/cabinCurriculum';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plus, Trash2, ChevronUp, ChevronDown, Save, ArrowLeft, GripVertical, Check,
  ImagePlus, X,
} from 'lucide-react';
import { apiClient } from '../services/apiClient';

const PHASE_TYPES = [
  { value: 'intro', tr: 'Giriş', en: 'Intro' },
  { value: 'teaching', tr: 'Anlatım', en: 'Teaching' },
  { value: 'multiple_choice', tr: 'Çoktan Seçmeli', en: 'Multiple Choice' },
  { value: 'vocabulary_time_race', tr: 'Kelime Yarışı', en: 'Vocabulary Race' },
  { value: 'match_pairs', tr: 'Eşleştirme', en: 'Match Pairs' },
  { value: 'fill_in_blank', tr: 'Boşluk Doldurma', en: 'Fill in Blank' },
  { value: 'drag_drop_sentence', tr: 'Sıralama', en: 'Drag & Drop' },
];

const i18n = (tr = '', en = '') => ({ tr, en });

/**
 * CardImageField — zaman yarisi kartina opsiyonel gorsel baglar.
 *
 * Yukleme mevcut `/upload/thumbnail` ucuna gider; o uc `db.files` kaydini
 * `file_type: "thumbnail"` ile acar ve thumbnail'lar auth'suz servis edilir.
 * Ogrencinin tarayicisi <img> ile Bearer token gonderemedigi icin BASKA bir
 * uc kullanilirsa gorsel 401 alir — bu yuzden uc degistirilmemeli.
 *
 * URL tam olarak saklanir (goreli degil): ayni icerigi mobil uygulama da
 * okuyor ve orada cozulecek bir origin yok.
 */
function CardImageField({ value, onChange, T }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiClient.post('/upload/thumbnail', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(`${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {value ? (
        <>
          <img src={value} alt={T('Kart görseli', 'Card image')} className="h-10 w-10 rounded object-contain border border-gray-200 bg-white" />
          <Button variant="ghost" size="icon" onClick={() => onChange('')} aria-label={T('Görseli kaldır', 'Remove image')}>
            <X size={14} className="text-red-500" />
          </Button>
        </>
      ) : (
        <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 px-2 py-1.5 rounded border border-dashed border-gray-300">
          <ImagePlus size={14} />
          {busy ? T('Yükleniyor…', 'Uploading…') : T('Görsel', 'Image')}
          <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={busy} />
        </label>
      )}
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}

function defaultContent(type) {
  switch (type) {
    case 'intro': return { text: i18n(), key_takeaways: [] };
    case 'teaching': return { sections: [{ title: i18n(), body: i18n(), examples: [], vocab: [] }] };
    case 'multiple_choice': return { questions: [{ question: i18n(), options: [{ text: i18n(), is_correct: true }, { text: i18n(), is_correct: false }], points: 10 }] };
    case 'vocabulary_time_race': return { cards: [{ front: '', back: '' }], time_limit_seconds: 60, lives: 3 };
    case 'match_pairs': return { pairs: [{ left: '', right: '' }, { left: '', right: '' }], shuffle_strategy: 'both', points_per_pair: 10 };
    case 'fill_in_blank': return { sentences: [{ text: '', answer: '', hint: '' }], points_per_sentence: 10 };
    case 'drag_drop_sentence': return { sentences: [{ words: [''], correct_order: [0], instruction: i18n() }], points_per_sentence: 10 };
    default: return {};
  }
}

function newPhase(type, order) {
  return {
    phase_id: `phase_${Math.random().toString(36).slice(2, 10)}`,
    type,
    title: i18n(),
    content: defaultContent(type),
    estimated_seconds: 60,
    xp_in_phase: type === 'multiple_choice' || type === 'vocabulary_time_race' ? 25 : 0,
    order,
  };
}

// ─── Reusable bilingual input ───────────────────────────────────────────────────
function I18nField({ label, value, onChange, multiline, placeholder = '' }) {
  const v = value || {};
  const Field = multiline ? Textarea : Input;
  return (
    <div className="space-y-1">
      {label && <Label className="text-xs font-semibold text-gray-600">{label}</Label>}
      <div className="grid grid-cols-2 gap-2">
        <Field value={v.tr ?? ''} onChange={(e) => onChange({ ...v, tr: e.target.value })} placeholder={`TR ${placeholder}`} rows={multiline ? 2 : undefined} />
        <Field value={v.en ?? ''} onChange={(e) => onChange({ ...v, en: e.target.value })} placeholder={`EN ${placeholder}`} rows={multiline ? 2 : undefined} />
      </div>
    </div>
  );
}

// ─── Per-type content editors ────────────────────────────────────────────────────
function ContentEditor({ type, content, onChange, lang }) {
  const c = content || {};
  const set = (patch) => onChange({ ...c, ...patch });
  const T = (tr, en) => (lang === 'tr' ? tr : en);

  // helpers for list arrays
  const updateItem = (key, idx, item) => set({ [key]: c[key].map((x, i) => (i === idx ? item : x)) });
  const addItem = (key, item) => set({ [key]: [...(c[key] || []), item] });
  const removeItem = (key, idx) => set({ [key]: c[key].filter((_, i) => i !== idx) });

  if (type === 'intro') {
    return (
      <div className="space-y-4">
        <I18nField label={T('Giriş metni', 'Intro text')} value={c.text} onChange={(v) => set({ text: v })} multiline />
        <div className="space-y-2">
          <Label className="text-xs font-semibold text-gray-600">{T('Önemli Noktalar', 'Key Takeaways')}</Label>
          {(c.key_takeaways || []).map((kt, i) => (
            <div key={i} className="flex gap-2 items-start">
              <I18nField value={kt} onChange={(v) => updateItem('key_takeaways', i, v)} />
              <Button variant="ghost" size="icon" onClick={() => removeItem('key_takeaways', i)}><Trash2 size={14} className="text-red-500" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => addItem('key_takeaways', i18n())}><Plus size={14} className="mr-1" />{T('Nokta Ekle', 'Add point')}</Button>
        </div>
      </div>
    );
  }

  if (type === 'teaching') {
    return (
      <div className="space-y-4">
        {(c.sections || []).map((s, i) => (
          <div key={i} className="rounded-lg border border-gray-200 p-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs font-bold text-gray-500">{T('Bölüm', 'Section')} {i + 1}</span>
              <Button variant="ghost" size="icon" onClick={() => removeItem('sections', i)}><Trash2 size={14} className="text-red-500" /></Button>
            </div>
            <I18nField label={T('Başlık', 'Title')} value={s.title} onChange={(v) => updateItem('sections', i, { ...s, title: v })} />
            <I18nField label={T('İçerik', 'Body')} value={s.body} onChange={(v) => updateItem('sections', i, { ...s, body: v })} multiline />
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => addItem('sections', { title: i18n(), body: i18n(), examples: [], vocab: [] })}><Plus size={14} className="mr-1" />{T('Bölüm Ekle', 'Add section')}</Button>
      </div>
    );
  }

  if (type === 'multiple_choice') {
    return (
      <div className="space-y-4">
        {(c.questions || []).map((q, qi) => (
          <div key={qi} className="rounded-lg border border-gray-200 p-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs font-bold text-gray-500">{T('Soru', 'Question')} {qi + 1}</span>
              <Button variant="ghost" size="icon" onClick={() => removeItem('questions', qi)}><Trash2 size={14} className="text-red-500" /></Button>
            </div>
            <I18nField label={T('Soru metni', 'Question text')} value={q.question} onChange={(v) => updateItem('questions', qi, { ...q, question: v })} multiline />
            <Label className="text-xs font-semibold text-gray-600">{T('Şıklar (yeşil tık = doğru)', 'Options (green = correct)')}</Label>
            {(q.options || []).map((o, oi) => (
              <div key={oi} className="flex gap-2 items-start">
                <Button
                  type="button"
                  variant={o.is_correct ? 'default' : 'outline'}
                  size="icon"
                  className="mt-0.5 shrink-0"
                  onClick={() => updateItem('questions', qi, { ...q, options: q.options.map((x, j) => ({ ...x, is_correct: j === oi })) })}
                  aria-label="mark-correct"
                >
                  <Check size={14} />
                </Button>
                <div className="flex-1"><I18nField value={o.text} onChange={(v) => updateItem('questions', qi, { ...q, options: q.options.map((x, j) => (j === oi ? { ...x, text: v } : x)) })} /></div>
                <Button variant="ghost" size="icon" onClick={() => updateItem('questions', qi, { ...q, options: q.options.filter((_, j) => j !== oi) })}><Trash2 size={14} className="text-red-500" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => updateItem('questions', qi, { ...q, options: [...(q.options || []), { text: i18n(), is_correct: false }] })}><Plus size={14} className="mr-1" />{T('Şık Ekle', 'Add option')}</Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => addItem('questions', { question: i18n(), options: [{ text: i18n(), is_correct: true }, { text: i18n(), is_correct: false }], points: 10 })}><Plus size={14} className="mr-1" />{T('Soru Ekle', 'Add question')}</Button>
      </div>
    );
  }

  if (type === 'vocabulary_time_race') {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs font-semibold text-gray-600">{T('Süre (sn)', 'Time (s)')}</Label><Input type="number" min={10} value={c.time_limit_seconds ?? 60} onChange={(e) => set({ time_limit_seconds: Number(e.target.value) })} /></div>
          <div className="space-y-1"><Label className="text-xs font-semibold text-gray-600">{T('Can', 'Lives')}</Label><Input type="number" min={1} value={c.lives ?? 3} onChange={(e) => set({ lives: Number(e.target.value) })} /></div>
        </div>
        <Label className="text-xs font-semibold text-gray-600">{T('Kartlar (soru → cevap)', 'Cards (front → back)')}</Label>
        {(c.cards || []).map((card, i) => (
          <div key={i} className="flex gap-2 items-center">
            <CardImageField value={card.image || ''} onChange={(url) => updateItem('cards', i, { ...card, image: url })} T={T} />
            <Input className="flex-1" value={card.front} onChange={(e) => updateItem('cards', i, { ...card, front: e.target.value })} placeholder={T('Soru', 'Front')} />
            <span className="text-gray-400">→</span>
            <Input className="flex-1" value={card.back} onChange={(e) => updateItem('cards', i, { ...card, back: e.target.value })} placeholder={T('Cevap', 'Back')} />
            <Button variant="ghost" size="icon" onClick={() => removeItem('cards', i)}><Trash2 size={14} className="text-red-500" /></Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => addItem('cards', { front: '', back: '' })}><Plus size={14} className="mr-1" />{T('Kart Ekle', 'Add card')}</Button>
      </div>
    );
  }

  if (type === 'match_pairs') {
    return (
      <div className="space-y-4">
        <Label className="text-xs font-semibold text-gray-600">{T('Eşleşen çiftler', 'Pairs')}</Label>
        {(c.pairs || []).map((p, i) => (
          <div key={i} className="flex gap-2 items-center">
            <Input className="flex-1" value={p.left} onChange={(e) => updateItem('pairs', i, { ...p, left: e.target.value })} placeholder={T('Sol', 'Left')} />
            <span className="text-gray-400">↔</span>
            <Input className="flex-1" value={p.right} onChange={(e) => updateItem('pairs', i, { ...p, right: e.target.value })} placeholder={T('Sağ', 'Right')} />
            <Button variant="ghost" size="icon" onClick={() => removeItem('pairs', i)}><Trash2 size={14} className="text-red-500" /></Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => addItem('pairs', { left: '', right: '' })}><Plus size={14} className="mr-1" />{T('Çift Ekle', 'Add pair')}</Button>
      </div>
    );
  }

  if (type === 'fill_in_blank') {
    return (
      <div className="space-y-4">
        <p className="text-xs text-gray-500">{T('Boşluğu ___ ile belirt (örn: "Acil kod: ___").', 'Mark the blank with ___ (e.g. "Code: ___").')}</p>
        {(c.sentences || []).map((s, i) => (
          <div key={i} className="rounded-lg border border-gray-200 p-3 space-y-2">
            <div className="flex justify-between items-center"><span className="text-xs font-bold text-gray-500">{T('Cümle', 'Sentence')} {i + 1}</span><Button variant="ghost" size="icon" onClick={() => removeItem('sentences', i)}><Trash2 size={14} className="text-red-500" /></Button></div>
            <Input value={s.text} onChange={(e) => updateItem('sentences', i, { ...s, text: e.target.value })} placeholder={T('Metin (___)', 'Text (___)')} />
            <div className="grid grid-cols-2 gap-2">
              <Input value={s.answer} onChange={(e) => updateItem('sentences', i, { ...s, answer: e.target.value })} placeholder={T('Cevap', 'Answer')} />
              <Input value={s.hint ?? ''} onChange={(e) => updateItem('sentences', i, { ...s, hint: e.target.value })} placeholder={T('İpucu (ops.)', 'Hint (opt.)')} />
            </div>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => addItem('sentences', { text: '', answer: '', hint: '' })}><Plus size={14} className="mr-1" />{T('Cümle Ekle', 'Add sentence')}</Button>
      </div>
    );
  }

  if (type === 'drag_drop_sentence') {
    return (
      <div className="space-y-4">
        <p className="text-xs text-gray-500">{T('Kelimeleri DOĞRU sırada gir; öğrenci karıştırılmış halini sıralar.', 'Enter words in CORRECT order; learner re-orders the shuffled set.')}</p>
        {(c.sentences || []).map((s, i) => (
          <div key={i} className="rounded-lg border border-gray-200 p-3 space-y-2">
            <div className="flex justify-between items-center"><span className="text-xs font-bold text-gray-500">{T('Cümle', 'Sentence')} {i + 1}</span><Button variant="ghost" size="icon" onClick={() => removeItem('sentences', i)}><Trash2 size={14} className="text-red-500" /></Button></div>
            <I18nField label={T('Talimat', 'Instruction')} value={s.instruction} onChange={(v) => updateItem('sentences', i, { ...s, instruction: v })} />
            <Label className="text-xs font-semibold text-gray-600">{T('Kelimeler (doğru sıra)', 'Words (correct order)')}</Label>
            {(s.words || []).map((w, wi) => (
              <div key={wi} className="flex gap-2 items-center">
                <span className="text-xs text-gray-400 w-5">{wi + 1}.</span>
                <Input className="flex-1" value={w} onChange={(e) => {
                  const words = s.words.map((x, j) => (j === wi ? e.target.value : x));
                  updateItem('sentences', i, { ...s, words, correct_order: words.map((_, k) => k) });
                }} />
                <Button variant="ghost" size="icon" onClick={() => {
                  const words = s.words.filter((_, j) => j !== wi);
                  updateItem('sentences', i, { ...s, words, correct_order: words.map((_, k) => k) });
                }}><Trash2 size={14} className="text-red-500" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => {
              const words = [...(s.words || []), ''];
              updateItem('sentences', i, { ...s, words, correct_order: words.map((_, k) => k) });
            }}><Plus size={14} className="mr-1" />{T('Kelime Ekle', 'Add word')}</Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => addItem('sentences', { words: [''], correct_order: [0], instruction: i18n() })}><Plus size={14} className="mr-1" />{T('Cümle Ekle', 'Add sentence')}</Button>
      </div>
    );
  }

  return <p className="text-sm text-gray-400">{T('Bu tip için editör yok.', 'No editor for this type.')}</p>;
}

// ─── Main page ───────────────────────────────────────────────────────────────────
export default function AdminLessonContent() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const toast = useToast();
  const T = (tr, en) => (lang === 'tr' ? tr : en);

  const { data: lesson, isLoading } = useAdminLessonContent(lessonId);
  const updateLesson = useUpdateLesson();

  const [phases, setPhases] = useState([]);
  const [selIdx, setSelIdx] = useState(0);

  useEffect(() => {
    if (lesson?.phases) {
      setPhases(lesson.phases);
      setSelIdx(0);
    }
  }, [lesson]);

  const lessonTitle = lesson ? (typeof lesson.title === 'string' ? lesson.title : lesson.title?.tr) : '';

  const updateContent = (idx, content) => setPhases((ps) => ps.map((p, i) => (i === idx ? { ...p, content } : p)));
  const updatePhaseField = (idx, patch) => setPhases((ps) => ps.map((p, i) => (i === idx ? { ...p, ...patch } : p)));

  const addPhase = (type) => {
    setPhases((ps) => {
      const next = [...ps, newPhase(type, ps.length + 1)];
      setSelIdx(next.length - 1);
      return next;
    });
  };
  const removePhase = (idx) => setPhases((ps) => {
    const next = ps.filter((_, i) => i !== idx).map((p, i) => ({ ...p, order: i + 1 }));
    setSelIdx((s) => Math.max(0, Math.min(s, next.length - 1)));
    return next;
  });
  const movePhase = (idx, dir) => setPhases((ps) => {
    const j = idx + dir;
    if (j < 0 || j >= ps.length) return ps;
    const next = [...ps];
    [next[idx], next[j]] = [next[j], next[idx]];
    return next.map((p, i) => ({ ...p, order: i + 1 }));
  });

  const save = async () => {
    try {
      const ordered = phases.map((p, i) => ({ ...p, order: i + 1 }));
      await updateLesson.mutateAsync({ id: lessonId, data: { phases: ordered } });
      toast.success(T('İçerik kaydedildi', 'Content saved'));
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const sel = phases[selIdx];
  const typeLabel = (t) => { const x = PHASE_TYPES.find((p) => p.value === t); return x ? T(x.tr, x.en) : t; };

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: T('Kabin Akademisi', 'Cabin Academy'), href: '/admin/cabin-academy' },
          { label: T('Müfredat', 'Curriculum'), href: '/admin/cabin-curriculum' },
          { label: T('İçerik', 'Content') },
        ]}
        title={`${T('İçerik', 'Content')}: ${lessonTitle}`}
        subtitle={lesson?.code}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/admin/cabin-curriculum')}><ArrowLeft size={16} className="mr-1" />{T('Geri', 'Back')}</Button>
            <Button onClick={save} disabled={updateLesson.isPending}><Save size={16} className="mr-1" />{T('Kaydet', 'Save')}</Button>
          </div>
        }
      />

      {isLoading ? (
        <LoadingState />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
          {/* Phase list */}
          <div className="space-y-2">
            {phases.map((p, i) => (
              <div
                key={p.phase_id || i}
                className={`rounded-lg border p-2.5 flex items-center gap-2 cursor-pointer ${i === selIdx ? 'border-sky-500 bg-sky-50' : 'border-gray-200 bg-white'}`}
                onClick={() => setSelIdx(i)}
              >
                <GripVertical size={14} className="text-gray-300 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-gray-700">{i + 1}. {typeLabel(p.type)}</p>
                  <p className="text-[11px] text-gray-400 truncate">{p.title?.tr || p.title?.en || '—'}</p>
                </div>
                <div className="flex flex-col">
                  <button onClick={(e) => { e.stopPropagation(); movePhase(i, -1); }} aria-label="up"><ChevronUp size={13} className="text-gray-400" /></button>
                  <button onClick={(e) => { e.stopPropagation(); movePhase(i, 1); }} aria-label="down"><ChevronDown size={13} className="text-gray-400" /></button>
                </div>
                <button onClick={(e) => { e.stopPropagation(); removePhase(i); }} aria-label="remove"><Trash2 size={13} className="text-red-400" /></button>
              </div>
            ))}
            <Select onValueChange={(v) => addPhase(v)} value="">
              <SelectTrigger className="w-full"><SelectValue placeholder={T('+ Phase Ekle', '+ Add phase')} /></SelectTrigger>
              <SelectContent>
                {PHASE_TYPES.map((pt) => <SelectItem key={pt.value} value={pt.value}>{T(pt.tr, pt.en)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Selected phase editor */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            {sel ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-sm font-bold text-gray-800">{typeLabel(sel.type)}</span>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">XP</Label>
                    <Input type="number" min={0} className="w-20 h-8" value={sel.xp_in_phase ?? 0} onChange={(e) => updatePhaseField(selIdx, { xp_in_phase: Number(e.target.value) })} />
                  </div>
                </div>
                <I18nField label={T('Phase Başlığı', 'Phase Title')} value={sel.title} onChange={(v) => updatePhaseField(selIdx, { title: v })} />
                <div className="border-t border-gray-100 pt-4">
                  <ContentEditor type={sel.type} content={sel.content} onChange={(content) => updateContent(selIdx, content)} lang={lang} />
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-12">{T('Soldan bir phase seç veya yeni ekle.', 'Select a phase or add one.')}</p>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
